import { FC, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { ModalBonus } from "components/modals/ModalBonus"
import { ModalPromoCode } from "components/modals/ModalPromoCode"
import { TopBar } from "components/navigation/TopBar"
import { SearchInput } from "components/search/SearchInput"
import { Table } from "components/tables/Table"

import { usePermissions } from "contexts/PermissionsContext"
import { useProps } from "contexts/PropsContext"

import { SortOrderBy, SortOrderType } from "constants/eums"
import { PERMISSIONS } from "constants/permissions"
import { tablePromoCodesColumns } from "constants/promo-codes"

import { BonusService } from "services/bonus/bonus.service"
import { promoCodeService } from "services/promoCode/promoCode.service"
import { CreatePromoCodePayload, EditPromoCodePayload } from "services/promoCode/type"

import { BonusType } from "types/api"

import { isMicroserviceTenant } from "config/featureFlags"

export const PromoCodes: FC = () => {
  const { t } = useTranslation(["translation"], { useSuspense: false })
  const { user } = useProps()
  const { has } = usePermissions()

  const [editRow, setEditRow] = useState(null)
  const [code, setCode] = useState<string>("")
  const [openEditModal, setOpenEditModal] = useState<boolean>(false)
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false)
  const [tableUpdatedAt, setTableUpdatedAt] = useState<Date | number>(Date.now())
  const [promoBonusList, setPromoBonusList] = useState<any>([])
  const [promoBonusSpinsList, setPromoBonusSpinsList] = useState<any>([])
  const [promoBonusFreebetList, setPromoBonusFreebetList] = useState<any>([])
  const [savedPromo, setSavedPromo] = useState({ id: "", title: "" })
  const [open, setOpen] = useState(false)
  const [offerBonusType, setOfferBonusType] = useState<BonusType>(BonusType.PROMO)

  const closeBonusModalHandler = (id?: string, title?: string): void => {
    // Only update savedPromo on actual save (id must be a real string, not a click event)
    if (id && typeof id === "string") setSavedPromo({ id, title: title ?? "" })
    setOpen(false)
  }

  const handleBonusModalCreate = (promoType: string) => {
    setOfferBonusType(promoType === "FREEBET" ? BonusType.FREEBET : promoType === "FREESPINS" ? BonusType.FREESPINS : BonusType.PROMO)
    setOpen(true)
  }

  useEffect(() => {
    const getPromoBonusList = async () => {
      const res = await BonusService.getBonusesList({ page: 1, pageSize: 100, filters: { type: BonusType.PROMO, active: true } })
      if (res.data) {
        setPromoBonusList(res.data)
      }

      const resSpins = await BonusService.getBonusesList({ page: 1, pageSize: 100, filters: { type: BonusType.FREESPINS, active: true } })
      if (resSpins.data) setPromoBonusSpinsList((prev: any) => [...prev, ...resSpins.data])

      const resFreebets = await BonusService.getBonusesList({ page: 1, pageSize: 100, filters: { type: BonusType.FREEBET, active: true } })
      if (resFreebets.data) setPromoBonusFreebetList(resFreebets.data)
    }
    getPromoBonusList()
  }, [savedPromo])

  const onEditRow = (row: any): void => {
    setSavedPromo({ id: "", title: "" })
    setEditRow(row)
    setOpenEditModal(true)
  }

  const editRowHandler = (): void => {
    setEditRow(null)
    setOpenEditModal(false)
    setSavedPromo({ id: "", title: "" })
  }

  const createPromoCodeHandler = (): void => {
    setOpenCreateModal(false)
    setSavedPromo({ id: "", title: "" })
  }

  const onEditModalSubmit = async ({ id, ...data }: EditPromoCodePayload & { id: string }): Promise<void> => {
    try {
      await promoCodeService.editPromoCode(id, data)
      setTableUpdatedAt(Date.now())
      toast.success("Promo code updated successfully")
    } catch (err) {
      toast.error("Failed")
    }
  }

  const onCreateModalSubmit = async ({
    id: _,
    ...data
  }: CreatePromoCodePayload & {
    id: null | string
  }): Promise<void> => {
    try {
      await promoCodeService.createPromoCode(data)
      setTableUpdatedAt(Date.now())
      toast.success("Promo code added successfully")
    } catch (err) {
      toast.error("Failed")
    }
  }

  const actions = [{ name: t("common.edit"), handler: onEditRow }]

  let defaultSortOrderBy: SortOrderBy | string = SortOrderBy.ID
  if (isMicroserviceTenant()) {
    defaultSortOrderBy = "code"
  }

  return (
    <div className="grid w-full gap-8 p-8 pt-0 grid-col-1">
      <TopBar title={t("pages.promoCodes.title")} />
      <div className="flex flex-row items-end justify-between">
        <div className="max-w-sm">
          <SearchInput placeholder={t("promo.promoCode")} onChange={setCode} />
        </div>
        {user && has(PERMISSIONS.PROMO_CODE_CREATE) && (
          <button
            className="btn btn-primary bg-transparent text-[rgba(169,78,206,1)] border-[rgba(169,78,206,0.8)] hover:bg-[rgba(169,78,206,0.7)] hover:border-transparent dark:text-white hover:text-white"
            onClick={() => {
              setSavedPromo({ id: "", title: "" })
              setOpenCreateModal(true)
            }}
          >
            {t("common.create")}
          </button>
        )}
      </div>
      <Table
        title={t("promo.promoCodes")}
        handler={promoCodeService.getPromoCodesTable}
        columns={tablePromoCodesColumns}
        actions={actions}
        updatedAt={tableUpdatedAt}
        filters={{ code }}
        defaultSortOrder={SortOrderType.ASC}
        defaultSortOrderBy={defaultSortOrderBy}
        onRowClick={(row: any) => onEditRow(row)}
      />
      <ModalPromoCode
        open={openEditModal}
        data={editRow}
        handler={editRowHandler}
        onSubmit={onEditModalSubmit}
        promoBonuses={promoBonusList}
        promoBonusesSpins={promoBonusSpinsList}
        promoBonusesFreebets={promoBonusFreebetList}
        handleBonusModalCreate={handleBonusModalCreate}
        savedPromo={savedPromo}
      />
      <ModalPromoCode
        open={openCreateModal}
        handler={createPromoCodeHandler}
        onSubmit={onCreateModalSubmit}
        promoBonuses={promoBonusList}
        promoBonusesSpins={promoBonusSpinsList}
        promoBonusesFreebets={promoBonusFreebetList}
        handleBonusModalCreate={handleBonusModalCreate}
        savedPromo={savedPromo}
      />
      <ModalBonus data={null} handler={closeBonusModalHandler} open={open} saveId={true} forcedBonusType={offerBonusType} />
    </div>
  )
}
